import { type GraphQLError } from 'graphql';

// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { type ApolloErrorInterop } from '@confluence/apollo-relay-interop-utils/entry-points/ApolloErrorInterop.ts';
import { processError } from '@confluence/graphql-error-processor/entry-points/GraphqlErrorProcessor.ts';

export type MutationErrorPolicy = 'none' | 'all' | 'ignore';

export type ApolloCompatibleMutationArgs<TData> = {
	fetchData: () => Promise<TData>;
	errorPolicy?: MutationErrorPolicy;
	onCompleted?: (data: TData) => void;
	onError?: (error: ApolloErrorInterop) => void;
	convertToApolloError: (error: Error) => ApolloErrorInterop | Promise<ApolloErrorInterop>;
};

export type ApolloCompatibleMutationResult<TData> = {
	data: TData;
	errors: readonly GraphQLError[];
};

export async function apolloCompatibleMutation<TData>({
	fetchData,
	errorPolicy = 'none',
	onCompleted,
	onError,
	convertToApolloError,
}: ApolloCompatibleMutationArgs<TData>): Promise<ApolloCompatibleMutationResult<TData>> {
	try {
		const data = await fetchData();

		onCompleted?.(data);
		return { data, errors: [] };
	} catch (error) {
		const apolloError = await Promise.resolve(convertToApolloError(error as Error));

		if (apolloError.graphQLErrors?.length) {
			apolloError.graphQLErrors.forEach((e) => {
				processError(e);
			});
		}
		onError?.(apolloError);

		if (errorPolicy === 'ignore') {
			return {
				data: undefined as TData,
				errors: [],
			};
		}

		if (errorPolicy === 'all') {
			return {
				data: undefined as TData,
				errors: apolloError.graphQLErrors || [],
			};
		}

		// errorPolicy === 'none': throw the error
		throw apolloError;
	}
}
